import {
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_AXIS_LABEL_FONT_SIZE
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { THEME_FONT_FAMILY } from "constants/style.constants";
import { EChartsOption } from "echarts";
import { YAXisOption } from "echarts/types/dist/shared";
import { wellStatusBucketDisplayLabel } from "entities/charts/eva/requests/wellStatus";
import { DEFAULT_TITLE_OPTIONS } from "entities/charts/options/title";
import { AxisLockOption, AxisLockPosition } from "types/charts/locks";
import { EvaChartStates } from "types/factory";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { DataEnum } from "models";

import { createChartLocks } from "../locks";
import {
  AXIS_LABEL_COLOR,
  AXIS_TITLE_COLOR,
  DEFAULT_Z_INDEX,
  VALUE_AXIS_LINE
} from "./constants";
import {
  GRID_BOTTOM_ROTATED,
  integerValueAxisOptions,
  resolveSeriesColorIgnoringCount,
  rigFracTooltip,
  rigFracXNameGap
} from "./rigFracActivity";
import { buildWellStatusGliders } from "./wellStatusGliders";
import {
  WELL_STATUS_BUCKET_LABELS,
  buildWellStatusItemTooltipFormatter,
  stackBucketTotals
} from "./wellStatusTooltip";

// Rotate the date labels once they stop fitting horizontally. Same threshold the
// rig/frac activity chart uses — beyond ~8 buckets the labels collide.
const ROTATE_LABELS_ABOVE = 8;

// Grid insets. Relationship to the rig/frac Activity chart's, since the two
// widgets share a look:
//  * GRID_BOTTOM_ROTATED is IMPORTED from it — not merely equal. The
//    TimeScaleSelector derives its axis-title overlay offset from that exact
//    value, so the two must move together.
//  * GRID_LEFT (66) and GRID_BOTTOM_FLAT (48) currently match its
//    GRID_LEFT_RIGHT / GRID_BOTTOM_FLAT. Kept local deliberately: they are the
//    same NUMBER, not the same requirement, and sharing them would silently
//    move this chart if rig/frac ever retunes its own.
//  * GRID_RIGHT and GRID_TOP genuinely differ — see each below.
const GRID_LEFT = 66;
// 24, not rig/frac's 66: that chart reserves the right inset for its secondary
// (Frac Crew Count) y-axis title. This chart has no right axis, so the inset
// only needs to stop the last bar touching the edge.
const GRID_RIGHT = 24;
// 56, not rig/frac's 46: this chart draws a VISIBLE title, and
// DEFAULT_TITLE_OPTIONS reserves 14px above and 10px below an 18px line. Also
// the anchor the MAX lock handle positions against.
const GRID_TOP = 56;
const GRID_BOTTOM_FLAT = 48;

// A y-axis option carrying the lock config the AxisContainer/AxisLocker read out
// of the persisted options JSON — the same shape TotalRateDate and Rig/Frac emit.
type YAXisOptionWithLock = Partial<YAXisOption> & {
  lock?: AxisLockOption;
  lockPosition?: AxisLockPosition;
  isTotalRateDateLockPosition?: boolean;
};

// Lock-handle placement, tuned for THIS grid (not TotalRateDate's): MAX just
// inside the top of the plot, MIN just below the axis line, both hugging the
// left axis.
//
// Derived from the grid's ACTUAL bottom rather than pinned to the rotated inset
// the way RIG_FRAC_PRIMARY_Y_LOCK_POSITION is. That constant is safe there because
// Activity vs Time always draws its full 13 buckets and so always rotates; this
// chart trims empty buckets at both ends, so a status held over a few periods
// legitimately renders 5-8 buckets with FLAT labels and a 48px bottom — where a
// pinned 95 would strand the MIN handle ~47px above the axis it should hug.
const wellStatusYLockPosition = (gridBottom: number): AxisLockPosition => ({
  min: { bottom: gridBottom - 5, left: 18 },
  max: { top: GRID_TOP + 2, left: 18 }
});

/**
 * Well Status Over Time. STACKED bars: one series per focus bin, all on
 * the same stack so each bar's height is the total well count in that status for
 * the bucket. Category x-axis of server-formatted bucket labels; integer value
 * y-axis with user-settable min/max locks.
 *
 * Reuses the rig/frac activity chart's tooltip, integer-axis and colour helpers
 * rather than restating them — the visual contract is deliberately identical, and
 * these are the pieces EVA-6690 (tooltip dismissal) and the integer-axis gotcha
 * were fixed in.
 *
 * Differences from the rig/frac Activity translator: no overlay line and no
 * secondary axis (this chart is a single stacked metric), and a visible chart
 * title.
 */
export function translateWellStatusOverTimeResponseToOptions(
  states: Partial<EvaChartStates>,
  statusTitle?: string,
  chartTitle?: string,
  focusTitle?: string,
  timeScale?: string
): EChartsOption {
  const { title, layout, series, seriesColors, chartType } = states;

  if (!series || series.length === 0) {
    return {};
  }

  const bucketLabels = (layout?.xAxis?.data ?? []).map((d) =>
    wellStatusBucketDisplayLabel(String(d ?? ""))
  );
  const labelsRotated = bucketLabels.length > ROTATE_LABELS_ABOVE;
  // Rotated labels need a deeper band; both depths already allow for the x
  // slider, whose transparent track overlays the top of that band.
  const gridBottom = labelsRotated ? GRID_BOTTOM_ROTATED : GRID_BOTTOM_FLAT;

  // Tallest STACK, not tallest series — the lock INPUT seeds from the summed bar
  // so the value the user sees pre-filled matches what the axis actually shows.
  const { raw: stackTotals, rounded: tooltipTotals } = stackBucketTotals(
    series,
    bucketLabels.length
  );
  const maxTotal = stackTotals.length > 0 ? Math.max(...stackTotals) : 0;

  const { lockedYMin, lockedYMax, lockedYMinType, lockedYMaxType } = createChartLocks({
    type: EvaChart.WellStatusOverTime,
    states
  });
  const yMin = lockedYMin ?? 0;
  // Axis MAX is set ONLY when the user locked it. Left undefined, ECharts
  // nice-rounds the ceiling up to the next whole tick (569 → 600) instead of
  // pinning the axis to the raw data max, which is what product asked for
  // ("Make y-axis max go to next whole value and no maximum value of data",
  // product). minInterval 1 keeps the unlocked axis safe on
  // all-zero data — ECharts falls back to [0, 1] rather than a degenerate scale.
  const yAxisMax = lockedYMax;
  // The lock input still displays a concrete number, so opening it on an
  // unlocked axis pre-fills the data's own ceiling rather than a blank.
  const yLockValue = lockedYMax ?? Math.max(1, Math.ceil(maxTotal));

  // ECharts stacks in series order, so Others first draws it under the named bins.
  const stackOrder = [...series].sort(
    (a, b) => Number(isOthersGroupLabel(b.label)) - Number(isOthersGroupLabel(a.label))
  );

  const barSeries = stackOrder.map((s) => ({
    type: "bar" as const,
    name: s.label,
    stack: "wellStatus",
    data: (s.y ?? []).map((v) => Number(v) || 0),
    itemStyle: {
      color: resolveSeriesColorIgnoringCount(
        s.label,
        seriesColors,
        s.style?.hexColor ?? "#000"
      )
    },
    z: DEFAULT_Z_INDEX
  }));

  const yAxis: YAXisOptionWithLock = {
    type: "value",
    // The status's human name ("Producing"), NOT the server's "<abbrev> Count" —
    // the bars are already counts, so the word adds nothing, and only the client
    // knows the readable name. Falls back to the server's title if none is given.
    name: statusTitle || layout?.yAxis?.title || "",
    nameLocation: "middle",
    nameGap: 46,
    nameRotate: 90,
    nameTextStyle: { fontSize: DEFAULT_AXIS_FONT_SIZE, color: AXIS_TITLE_COLOR },
    min: yMin,
    max: yAxisMax,
    // Counts are whole wells: minInterval 1 stops fractional ticks.
    ...integerValueAxisOptions,
    // ECharts draws an axis line on a CATEGORY axis by default but not on a value
    // one, so without this the count axis had no line while the time axis beside
    // it did. Rig/frac never hits this because it builds its y-axis through
    // createEntityState, which injects the same default.
    axisLine: VALUE_AXIS_LINE,
    // Explicit splitLine/minorSplitLine objects. The screenshot transform only
    // flips `splitLine.show` when the object ALREADY EXISTS, so an axis relying
    // on ECharts' implicit default left the Y-Axis Major toggle a silent no-op
    // (the same trap rig/frac hit on its category x-axis).
    splitLine: { show: true },
    minorTick: { show: false },
    minorSplitLine: { show: false, lineStyle: { color: "#F2F2F2" } },
    // Lock handles. AxisContainer/AxisLocker read these off the persisted options
    // JSON. override false — a locked max is applied to the axis directly, so no
    // dispatch back into the store is needed.
    lock: {
      show: true,
      min: { value: yMin, type: lockedYMinType ?? DataEnum.Number },
      max: {
        value: yLockValue,
        type: lockedYMaxType ?? DataEnum.Number,
        override: false
      }
    },
    lockPosition: wellStatusYLockPosition(gridBottom),
    isTotalRateDateLockPosition: true
  };

  return {
    // MUST be emitted. The screenshot transform keys its lean-widget path off
    // `options.chartType`; without it this chart fell through to the WELLS
    // grid/nameGap/spacing overrides, which re-measure against the chart
    // instance and pushed the axis title into the rotated label band ("spacing
    // still doesn't work").
    chartType,
    // Inherited by every piece of text ECharts draws — axis labels, axis names,
    // the title — the way the wells charts get it from DEFAULT_CHART_OPTIONS.
    // These translators build their options from scratch, so without it the chart
    // fell back to the browser's default sans and read differently from every
    // other chart on the dashboard.
    textStyle: { fontFamily: THEME_FONT_FAMILY },
    // Bar transitions are off across this widget (product). Also matches
    // DEFAULT_CHART_OPTIONS, which the wells charts build on — they have
    // never animated.
    animation: false,
    // "Well Status vs Time (Monthly)". Styling and placement come from
    // DEFAULT_TITLE_OPTIONS — the same 18px bold #041C2C, left-aligned with the
    // wells charts' padding — rather than a bespoke caption, so this widget's
    // title is indistinguishable from theirs. The caller builds the TEXT (the
    // granularity is a request property, not a response one); the server title
    // is the fallback so the option is never an empty caption.
    title: {
      ...DEFAULT_TITLE_OPTIONS,
      text: chartTitle || title || "",
      show: Boolean(chartTitle || title)
    },
    grid: {
      top: GRID_TOP,
      left: GRID_LEFT,
      right: GRID_RIGHT,
      bottom: gridBottom,
      containLabel: false
    },
    // Gliders on both axes (x = time buckets, y = well count).
    dataZoom: buildWellStatusGliders({
      top: GRID_TOP,
      left: GRID_LEFT,
      bottom: gridBottom
    }),
    tooltip: {
      ...rigFracTooltip,
      trigger: "item",
      axisPointer: { type: "none" },
      formatter: buildWellStatusItemTooltipFormatter({
        // layout.xAxis.title is "Time" here, which is not a group-by.
        groupByLabel: focusTitle,
        groupFrom: "series",
        metricLabel: statusTitle || layout?.yAxis?.title,
        totals: tooltipTotals,
        bucketLabel: (timeScale && WELL_STATUS_BUCKET_LABELS[timeScale]) || "Date"
      })
    },
    xAxis: {
      type: "category",
      data: bucketLabels,
      name: layout?.xAxis?.title ?? "Time",
      nameLocation: "middle",
      nameGap: labelsRotated
        ? rigFracXNameGap(bucketLabels, DEFAULT_AXIS_LABEL_FONT_SIZE)
        : 28,
      nameTextStyle: { fontSize: DEFAULT_AXIS_FONT_SIZE, color: AXIS_TITLE_COLOR },
      axisLabel: {
        // Slanted when crowded ("add incline text").
        rotate: labelsRotated ? 45 : 0,
        fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE,
        // The VALUE axis gets this via integerValueAxisOptions; a category axis
        // builds its own axisLabel, so without it the two axes on one chart
        // rendered their ticks in different colours.
        color: AXIS_LABEL_COLOR,
        hideOverlap: true
      },
      // Off by default — a category axis draws no vertical gridlines — but
      // PRESENT, so the screenshot X-Axis Major toggle has an object to flip
      // rather than being a no-op.
      splitLine: { show: false },
      axisTick: { alignWithLabel: true }
    },
    yAxis,
    series: barSeries
  } as EChartsOption;
}
